// frontend_app/src/lib/facets.js
//
// Post-search filtering. Counts follow the usual faceted-search rule: each
// facet's counts apply every *other* active filter, so ticking a value never
// hides the remaining options in the same facet.

import { SPECIMEN_FACET_ORDER, searchTextOf } from './resultModel';

export const REGION_ORDER = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT', 'National', 'Custom'];

export const FACETS = {
  lab: {
    label: 'Lab',
    values: (m) => [m.labGroup],
  },
  region: {
    label: 'State',
    values: (m) => [m.region || 'Other'],
    order: REGION_ORDER,
  },
  specimen: {
    label: 'Specimen',
    values: (m) => (m.specimenCategories.length ? m.specimenCategories : ['Not stated']),
    order: SPECIMEN_FACET_ORDER,
  },
  referral: {
    label: 'Performed',
    values: (m) => [m.referral === 'referred' ? 'Referred out' : 'In-house'],
    order: ['In-house', 'Referred out'],
  },
};

export const FACET_KEYS = Object.keys(FACETS);

export const emptyFilters = () => FACET_KEYS.reduce((acc, key) => ({ ...acc, [key]: [] }), {});

export const activeFilterCount = (filters, refine = '') =>
  FACET_KEYS.reduce((sum, key) => sum + (filters[key] || []).length, 0) + (refine.trim() ? 1 : 0);

const passesFacet = (model, key, selected) =>
  !selected || selected.length === 0 || FACETS[key].values(model).some((v) => selected.includes(v));

export const passesFilters = (model, filters, exceptKey = null) =>
  FACET_KEYS.every((key) => key === exceptKey || passesFacet(model, key, filters[key]));

/** Refine matches result *values* only (the old version also matched JSON keys). */
export const applyRefine = (models, refine) => {
  const needle = (refine || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!needle) return models;
  return models.filter((m) => searchTextOf(m).includes(needle));
};

export const applyFilters = (models, filters) => models.filter((m) => passesFilters(m, filters));

const sortValues = (key, entries) => {
  const order = FACETS[key].order;
  return entries.sort((a, b) => {
    if (order) {
      const ai = order.indexOf(a.value);
      const bi = order.indexOf(b.value);
      if (ai !== bi) return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    }
    return a.value.localeCompare(b.value);
  });
};

/**
 * `{ lab: [{ value, count }], region: [...], ... }` for the given models.
 * Selected values are always listed, even when their count drops to zero.
 */
export const computeFacetCounts = (models, filters) => {
  const result = {};
  FACET_KEYS.forEach((key) => {
    const counts = new Map();
    models.forEach((m) => {
      if (!passesFilters(m, filters, key)) return;
      FACETS[key].values(m).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
    });
    (filters[key] || []).forEach((v) => { if (!counts.has(v)) counts.set(v, 0); });
    result[key] = sortValues(key, Array.from(counts, ([value, count]) => ({ value, count })));
  });
  return result;
};
